import { useState } from 'react';
import {
  CommandCenterSidebar,
  SIDEBAR_WIDTH_CLASS,
} from '@/features/manager-command-center/components/CommandCenterSidebar';
import { PlatformGlyph } from '@/features/manager-breaks-demo/components/PlatformGlyph';
import {
  PLATFORM_ICON_BG,
  PLATFORM_LABEL,
  PLATFORM_TEXT,
} from '@/features/manager-breaks-demo/components/break-styles';
import {
  DEFAULT_PAST_PROJECT,
  PAST_PROJECTS,
  getPastProjectById,
} from '../data/retrospectives-demo-dataset';
import type {
  EvidenceSource,
  PastProject,
  PlaybookAccent,
  PlaybookSource,
  RetroPlaybook,
  RetroSignal,
  RetroSignalKind,
} from '../types/retrospective';

/** Contained library width - wide rows, never edge-to-edge on ultrawide. */
const LIBRARY_WIDTH = 'max-w-[1180px]';

/** The deep retrospective reads as one centered narrative column. */
const NARRATIVE_WIDTH = 'mx-auto max-w-[940px]';

/** RTL-safe horizontal centering on an absolutely-positioned marker. */
const CENTER_X = '-translate-x-1/2 rtl:translate-x-1/2';

/* ─────────────────────────── Shared bits ──────────────────────────── */

/** Numbered editorial section header: index · label, with a trailing hairline. */
function SectionEyebrow({ index, label }: { index: string; label: string }) {
  return (
    <div className="mb-8 flex items-center gap-4">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
        <span className="text-[#0089bd]">{index}</span>
        <span className="mx-2 text-zinc-300">·</span>
        {label}
      </span>
      <span className="h-px flex-1 bg-zinc-200/80" />
    </div>
  );
}

/** Outcome chip - rose when the project slipped, emerald when it held. */
function OutcomeBadge({ project }: { project: PastProject }) {
  const isLate = project.outcome === 'late';
  return (
    <span
      className={`inline-flex w-max items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
        isLate
          ? 'border-rose-100 bg-rose-50 text-rose-700'
          : 'border-emerald-100 bg-emerald-50 text-emerald-700'
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isLate ? 'bg-rose-500' : 'bg-emerald-500'
        }`}
      />
      {isLate ? `Shipped ${project.slipLabel} late` : 'Shipped on time'}
    </span>
  );
}

/* ─────────────────────── Library (dossier rows) ───────────────────── */

/** One footer stat on a dossier card. */
function CardStat({
  label,
  value,
  unit,
  sub,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
  tone: string;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
        {label}
      </p>
      <p className="flex items-baseline gap-1">
        <span className={`text-xl font-semibold tracking-tight ${tone}`}>
          {value}
        </span>
        {unit && (
          <span className="text-xs font-medium text-zinc-400">{unit}</span>
        )}
      </p>
      {sub && (
        <p className="mt-0.5 truncate text-[11px] leading-snug text-zinc-500">
          {sub}
        </p>
      )}
    </div>
  );
}

/** One half-width dossier card - the whole surface opens the deep retrospective. */
function DossierCard({
  project,
  onOpen,
}: {
  project: PastProject;
  onOpen: (id: string) => void;
}) {
  const isLate = project.outcome === 'late';
  return (
    <button
      type="button"
      onClick={() => onOpen(project.id)}
      className="group relative flex w-full flex-col overflow-hidden rounded-[2rem] border border-zinc-100 bg-white p-7 text-start shadow-[0_24px_60px_-50px_rgba(16,27,58,0.5)] transition-all hover:-translate-y-0.5 hover:shadow-[0_30px_70px_-44px_rgba(16,27,58,0.5)] md:p-8"
    >
      {/* Hover accent rail */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-7 start-0 w-1 origin-top scale-y-0 rounded-full transition-transform duration-300 group-hover:scale-y-100 ${
          isLate ? 'bg-rose-400' : 'bg-emerald-400'
        }`}
      />

      {/* Identity */}
      <div className="flex items-center justify-between gap-3">
        <OutcomeBadge project={project} />
        <span className="text-[11px] font-medium text-zinc-400">
          {project.codename}
        </span>
      </div>
      <h3 className="mt-4 text-xl font-semibold tracking-tight text-zinc-900 transition-colors group-hover:text-[#0089bd] md:text-2xl">
        {project.name}
      </h3>
      <p className="mt-1 text-sm text-zinc-500">{project.team}</p>
      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-500">
        {project.summary}
      </p>

      {/* Cost + delay read */}
      <div className="mt-6 flex w-full items-end justify-between gap-4 border-t border-zinc-100 pt-5">
        <div className="grid flex-1 grid-cols-3 gap-4">
          <CardStat
            label="Cost"
            value={isLate ? formatDollars(project.dollarCost) : '$0'}
            tone={isLate ? 'text-rose-600' : 'text-emerald-600'}
          />
          <CardStat
            label="Work hours"
            value={String(project.workHours)}
            unit="hrs"
            tone="text-zinc-900"
          />
          <CardStat
            label="Delivery delay"
            value={isLate ? project.slipLabel : 'On time'}
            tone={isLate ? 'text-rose-600' : 'text-emerald-600'}
          />
        </div>
        <span className="pb-1 text-zinc-300 transition-colors group-hover:text-[#0089bd]">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6 rtl:rotate-180"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </span>
      </div>
    </button>
  );
}

function RetroLibrary({
  projects,
  onOpen,
}: {
  projects: PastProject[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className={`mx-auto w-full ${LIBRARY_WIDTH}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">
        Knowledge archive
      </p>
      <h1 className="mt-2 text-4xl font-semibold tracking-tight text-zinc-900 md:text-5xl">
        Retrospectives
      </h1>
      <p className="mt-3 max-w-2xl text-lg leading-relaxed text-zinc-500">
        Every finished project, reconstructed end to end - what surfaced, what
        it cost, and the guardrails Intera extracted so it can&apos;t recur.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {projects.map((project) => (
          <DossierCard key={project.id} project={project} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────── 01 · The case (cost + timeline) ──────────────── */

/** Compact dollar label, e.g. 39600 -> "$40K". */
function formatDollars(amount: number): string {
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount}`;
}

/** One headline metric tile. */
function CaseStat({
  label,
  value,
  unit,
  sub,
  tone,
}: {
  label: string;
  value: string;
  unit?: string;
  sub: string;
  tone: string;
}) {
  return (
    <div className="rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)]">
      <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
        {label}
      </p>
      <p className="mt-3 flex items-baseline gap-1.5">
        <span className={`text-4xl font-semibold tracking-tight ${tone}`}>
          {value}
        </span>
        {unit && (
          <span className="text-sm font-medium text-zinc-400">{unit}</span>
        )}
      </p>
      <p className="mt-2 text-sm text-zinc-500">{sub}</p>
    </div>
  );
}

/** Node chrome per signal kind - the escalation read, calm to critical. */
const NODE_STYLE: Record<RetroSignalKind, string> = {
  'early-warning': 'h-3 w-3 bg-zinc-300 ring-2 ring-white',
  'intera-flag': 'h-4 w-4 bg-amber-500 ring-[3px] ring-white',
  drift: 'h-3.5 w-3.5 bg-amber-500 ring-2 ring-white',
  incident: 'h-5 w-5 bg-rose-500 ring-4 ring-rose-100',
  ship: 'h-3 w-3 bg-zinc-400 ring-2 ring-white',
};

/** Label / eyebrow tint per signal kind. */
const LABEL_TONE: Record<RetroSignalKind, string> = {
  'early-warning': 'text-zinc-400',
  'intera-flag': 'text-amber-600',
  drift: 'text-amber-600',
  incident: 'text-rose-600',
  ship: 'text-zinc-500',
};

/** Small detail-row dot per kind. */
const KIND_DOT: Record<RetroSignalKind, string> = {
  'early-warning': 'bg-zinc-300',
  'intera-flag': 'bg-amber-500',
  drift: 'bg-amber-500',
  incident: 'bg-rose-500',
  ship: 'bg-zinc-400',
};

/** A node on the proportional time track, placed by its position. */
function TrackNode({ signal }: { signal: RetroSignal }) {
  return (
    <div
      className={`absolute top-3 -translate-y-1/2 ${CENTER_X}`}
      style={{ insetInlineStart: `${signal.position}%` }}
    >
      <span
        className={`relative block rounded-full ${NODE_STYLE[signal.kind]}`}
      >
        {signal.kind === 'intera-flag' && (
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-amber-400/50" />
        )}
      </span>
      <span
        className={`absolute top-4 whitespace-nowrap text-[10px] font-bold uppercase tracking-wider ${CENTER_X} start-1/2 ${
          LABEL_TONE[signal.kind]
        }`}
      >
        {signal.dateLabel}
      </span>
    </div>
  );
}

/** A compact event under the track - date, what surfaced, one line of read. */
function SignalRow({ signal }: { signal: RetroSignal }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${KIND_DOT[signal.kind]}`} />
        <span
          className={`text-[10px] font-bold uppercase tracking-wider ${
            LABEL_TONE[signal.kind]
          }`}
        >
          {signal.dateLabel}
        </span>
      </div>
      <h4 className="mt-1.5 text-sm font-semibold leading-snug text-zinc-900">
        {signal.label}
      </h4>
      <p className="mt-1 text-[12px] leading-relaxed text-zinc-500">
        {signal.detail}
      </p>
    </div>
  );
}

function CaseSection({ project }: { project: PastProject }) {
  const isLate = project.outcome === 'late';
  return (
    <section>
      <SectionEyebrow index="01" label="The case" />

      <div className="grid gap-4 md:grid-cols-3">
        <CaseStat
          label="Cost to the company"
          value={formatDollars(project.dollarCost)}
          sub="Loaded engineering time burned on the gap"
          tone="text-rose-600"
        />
        <CaseStat
          label="Work hours spent"
          value={String(project.workHours)}
          unit="hours"
          sub="Logged absorbing the gap"
          tone="text-zinc-900"
        />
        <CaseStat
          label="Delivery delay"
          value={isLate ? project.slipLabel : 'On time'}
          sub={isLate ? 'Past the committed date' : 'Held the committed date'}
          tone={isLate ? 'text-rose-600' : 'text-emerald-600'}
        />
      </div>

      {/* Escalation timeline */}
      <div className="mt-5 rounded-[2rem] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-400">
            How it escalated
          </p>
          <p className="text-xs font-medium text-zinc-400">
            First gap surfaced · {project.firstGapLabel}
          </p>
        </div>

        {/* Proportional time track - calm to critical. */}
        <div className="relative mx-1 mb-10 mt-8 h-10">
          <div className="absolute inset-x-0 top-3 h-0.5 -translate-y-1/2 rounded-full bg-[linear-gradient(90deg,#cbd5e1_0%,#f59e0b_62%,#e11d48_88%)] rtl:bg-[linear-gradient(270deg,#cbd5e1_0%,#f59e0b_62%,#e11d48_88%)]" />
          {project.signals.map((signal) => (
            <TrackNode key={signal.id} signal={signal} />
          ))}
        </div>

        {/* The events, in order. */}
        <div className="grid gap-x-8 gap-y-6 border-t border-zinc-100 pt-7 sm:grid-cols-2 lg:grid-cols-3">
          {project.signals.map((signal) => (
            <SignalRow key={signal.id} signal={signal} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────── 02 · Synthesis (fragmented → one) ───────────────── */

/** One scattered source tile, with the volume Intera scanned from it. */
function SourceTile({ source }: { source: EvidenceSource }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-2.5 shadow-[0_10px_30px_-26px_rgba(16,27,58,0.6)]">
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          PLATFORM_ICON_BG[source.platform]
        }`}
      >
        <PlatformGlyph
          platform={source.platform}
          className={`h-4 w-4 ${PLATFORM_TEXT[source.platform]}`}
        />
      </span>
      <div className="min-w-0">
        <p className="flex items-baseline gap-1.5">
          <span className="text-base font-bold tracking-tight text-zinc-900">
            {source.count}
          </span>
          <span className="text-xs font-medium text-zinc-400">
            {PLATFORM_LABEL[source.platform]}
          </span>
        </p>
        <p className="text-[11px] leading-tight text-zinc-500">{source.unit}</p>
      </div>
    </div>
  );
}

/** Faint lines fanning from each scattered source into the single insight. */
function ConvergenceLines({ count }: { count: number }) {
  const lines = Array.from({ length: count }, (_, index) => {
    const y = ((index + 0.5) / count) * 100;
    return { id: index, y };
  });
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="h-full w-full rtl:-scale-x-100"
      aria-hidden="true"
    >
      {lines.map((line, index) => (
        <path
          key={line.id}
          d={`M0 ${line.y} C 55 ${line.y}, 55 50, 100 50`}
          fill="none"
          stroke={index === 1 ? '#00AEEF' : '#e4e9f0'}
          strokeWidth={index === 1 ? 0.9 : 0.6}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

/** The single insight every scattered source resolves into. */
function InsightNode() {
  return (
    <div className="relative">
      <span className="absolute inset-0 animate-ping rounded-full bg-[#00AEEF]/20 [animation-duration:3s]" />
      <div className="relative h-32 w-32 rounded-full bg-[linear-gradient(135deg,#00AEEF_0%,#1C3870_100%)] p-[2px] shadow-[0_20px_50px_-20px_rgba(0,174,239,0.6)]">
        <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-white text-center">
          <PlatformGlyph platform="intera" className="h-6 w-6 text-[#0089bd]" />
          <span className="mt-1.5 text-sm font-bold tracking-tight text-[#0E1B3A]">
            1 root cause
          </span>
          <span className="mt-0.5 text-[10px] font-medium text-[#0089bd]">
            correlated
          </span>
        </div>
      </div>
    </div>
  );
}

function SynthesisSection({ project }: { project: PastProject }) {
  return (
    <section>
      <SectionEyebrow index="02" label="How Intera gathered it" />

      <p className="max-w-2xl text-lg leading-relaxed text-zinc-600">
        {project.evidenceHeadline}
      </p>

      <div className="mt-8 overflow-hidden rounded-[2rem] border border-zinc-100 bg-white p-8 shadow-[0_24px_60px_-52px_rgba(16,27,58,0.5)] md:p-10">
        <div className="flex flex-col items-stretch gap-6 lg:flex-row lg:items-center">
          {/* Scattered sources */}
          <div className="flex flex-col gap-3 lg:w-64">
            {project.evidenceSources.map((source) => (
              <SourceTile key={source.platform} source={source} />
            ))}
          </div>

          {/* Convergence */}
          <div className="relative hidden min-h-[180px] flex-1 lg:block">
            <ConvergenceLines count={project.evidenceSources.length} />
          </div>

          {/* Mobile connector */}
          <div className="flex justify-center text-zinc-300 lg:hidden">
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6 rotate-90"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </div>

          {/* The single insight */}
          <div className="flex shrink-0 justify-center lg:w-48">
            <InsightNode />
          </div>
        </div>

        <p className="mt-8 border-t border-zinc-100 pt-6 text-sm text-zinc-500">
          No single tool held the story. Intera correlated the fragments into
          one cause no dashboard could surface on its own.
        </p>
      </div>
    </section>
  );
}

/* ───────────────────────── 03 · The RCA ───────────────────────────── */

function RcaSection({ project }: { project: PastProject }) {
  return (
    <section>
      <SectionEyebrow index="03" label="Root-cause analysis" />

      <div className="space-y-5 text-[15px] leading-relaxed text-zinc-600 md:text-base">
        {project.rcaParagraphs.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>

      {/* The single root-cause line, called out. */}
      <div className="mt-8 rounded-3xl border border-[#00AEEF]/15 bg-[#00AEEF]/[0.04] p-8 text-center md:p-10">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0089bd]">
          The root cause
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-xl font-medium leading-snug tracking-tight text-[#0E1B3A] md:text-2xl">
          {project.rootCauseLine}
        </p>
      </div>
    </section>
  );
}

/* ───────────── 04 · Procedures extracted (cause → effect) ──────────── */

/** Enforced-side tint per accent. */
const ENFORCED_TONE: Record<
  PlaybookAccent,
  { dot: string; label: string; text: string }
> = {
  emerald: {
    dot: 'bg-emerald-500',
    label: 'text-emerald-600',
    text: 'text-emerald-900',
  },
  cyan: { dot: 'bg-[#00AEEF]', label: 'text-[#0089bd]', text: 'text-sky-900' },
};

/** Small chip naming the source kind, e.g. "Debrief" / "Slack" / "RCA". */
function SourceTag({ children }: { children: string }) {
  return (
    <span className="ms-auto rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
      {children}
    </span>
  );
}

/**
 * The real source artifact Intera read - the hero of each procedure card. A
 * Slack message keeps its chat-bubble shape, a debrief keeps its file-and-
 * blockquote look, and an RCA shows the finding the rule was drawn from. This
 * is the "where it came from" the guardrail below grew out of.
 */
function ProcedureArtifact({
  source,
  reason,
}: {
  source: PlaybookSource;
  reason: string;
}) {
  if (source.kind === 'slack') {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_10px_30px_-26px_rgba(16,27,58,0.6)]">
        <div className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${PLATFORM_ICON_BG.slack}`}
          >
            <PlatformGlyph
              platform="slack"
              className={`h-3.5 w-3.5 ${PLATFORM_TEXT.slack}`}
            />
          </span>
          <span className="text-xs font-semibold text-zinc-700">
            {source.reference}
          </span>
          <SourceTag>Slack</SourceTag>
        </div>
        {source.quote && (
          <p className="mt-3 rounded-2xl rounded-ss-sm bg-zinc-50 px-4 py-3 text-sm leading-relaxed text-zinc-700">
            “{source.quote}”
          </p>
        )}
        {source.attribution && (
          <p className="mt-2 ps-1 text-[11px] font-medium text-zinc-400">
            {source.attribution}
          </p>
        )}
      </div>
    );
  }

  if (source.kind === 'debrief') {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-zinc-500 ring-1 ring-inset ring-zinc-200">
            <svg
              viewBox="0 0 24 24"
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M14 3v4a1 1 0 0 0 1 1h4" />
              <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
              <path d="M9 13h5M9 17h3" />
            </svg>
          </span>
          <span className="font-mono text-xs font-medium text-zinc-600">
            {source.reference}
          </span>
          <SourceTag>Debrief</SourceTag>
        </div>
        {source.quote && (
          <blockquote className="mt-3 border-s-2 border-zinc-300 ps-3 text-sm italic leading-relaxed text-zinc-600">
            “{source.quote}”
          </blockquote>
        )}
        {source.attribution && (
          <p className="mt-2 ps-3 text-[11px] font-medium text-zinc-400">
            {source.attribution}
          </p>
        )}
      </div>
    );
  }

  // rca - no human quote; show the finding the guardrail was drawn from.
  return (
    <div className="rounded-2xl border border-[#00AEEF]/20 bg-[#00AEEF]/[0.04] p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white ring-1 ring-inset ring-[#00AEEF]/20">
          <PlatformGlyph
            platform="intera"
            className="h-3.5 w-3.5 text-[#0089bd]"
          />
        </span>
        <span className="text-xs font-semibold text-zinc-700">
          {source.label}
        </span>
        <SourceTag>RCA</SourceTag>
      </div>
      <p className="mt-3 ps-1 text-sm leading-relaxed text-zinc-600">
        <span className="font-semibold text-zinc-700">Finding - </span>
        {reason}
      </p>
    </div>
  );
}

/**
 * One procedure as a left-to-right read: the source artifact Intera lifted it
 * from (a Slack message, a debrief, the RCA) on the left, and the guardrail it
 * now enforces on the right - with Intera as the transform between them. Not a
 * card of its own; it's a row in the shared procedures ledger.
 */
function ProcedureRow({ playbook }: { playbook: RetroPlaybook }) {
  const enforced = ENFORCED_TONE[playbook.accent];
  return (
    <div className="grid items-center gap-5 py-10 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-8">
      {/* Left: the source artifact Intera read */}
      <ProcedureArtifact source={playbook.source} reason={playbook.reason} />

      {/* Middle: Intera transforms the artifact into a guardrail */}
      <div className="flex items-center justify-center gap-2 md:flex-col">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#00AEEF]/10 px-2.5 py-1 text-[10px] font-semibold text-[#0089bd]">
          <PlatformGlyph platform="intera" className="h-3 w-3" />
          Intera
        </span>
        <svg
          viewBox="0 0 24 24"
          className="h-5 w-5 text-[#00AEEF]/50 max-md:rotate-90 md:rtl:rotate-180"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </div>

      {/* Right: the guardrail now enforced */}
      <div>
        <div className="flex items-center gap-2">
          <span
            className={`flex h-6 w-6 items-center justify-center rounded-full ${
              playbook.accent === 'emerald' ? 'bg-emerald-100' : 'bg-sky-100'
            }`}
          >
            <svg
              viewBox="0 0 24 24"
              className={`h-3.5 w-3.5 ${enforced.label}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider ${enforced.label}`}
          >
            Now enforced
          </span>
        </div>
        <h4 className="mt-2.5 text-lg font-semibold tracking-tight text-zinc-900">
          {playbook.name}
        </h4>
        <p className="mt-1 text-sm leading-relaxed text-zinc-600">
          {playbook.outcome}
        </p>
      </div>
    </div>
  );
}

function ProceduresSection({ project }: { project: PastProject }) {
  return (
    <section>
      <SectionEyebrow index="04" label="Procedures extracted" />

      <p className="max-w-2xl text-lg leading-relaxed text-zinc-600">
        Intera turned this project into guardrails now running across the org -
        some read from the root-cause analysis, others lifted from the
        team&apos;s own debrief and from a passing &ldquo;next time we
        should…&rdquo; note in Slack. Each one a failure turned into something
        that can&apos;t happen again.
      </p>

      <div className="mt-6 divide-y divide-zinc-200/70 rounded-3xl border border-zinc-100 bg-white px-6 shadow-[0_24px_60px_-54px_rgba(16,27,58,0.5)] md:px-8">
        {project.playbooks.map((playbook) => (
          <ProcedureRow key={playbook.id} playbook={playbook} />
        ))}
      </div>

      <p className="mt-6 flex items-center gap-2 text-sm font-medium text-zinc-400">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        {project.guardingLabel}
      </p>
    </section>
  );
}

/* ─────────────────────── Deep retrospective ───────────────────────── */

function RetroDetail({
  project,
  onBack,
}: {
  project: PastProject;
  onBack: () => void;
}) {
  return (
    <div className={NARRATIVE_WIDTH}>
      <button
        type="button"
        onClick={onBack}
        className="group flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-800"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4 transition-transform group-hover:-translate-x-0.5 rtl:rotate-180 rtl:group-hover:translate-x-0.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M11 18l-6-6 6-6" />
        </svg>
        Back to library
      </button>

      {/* Hero */}
      <header className="mt-10">
        <div className="flex items-center gap-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0089bd]">
            {project.codename}
          </span>
          <span className="text-[11px] font-medium text-zinc-400">
            {project.team}
          </span>
        </div>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-zinc-900 md:text-5xl">
          {project.name}
        </h1>
        <div className="mt-4">
          <OutcomeBadge project={project} />
        </div>
        <p className="mt-6 border-s-2 border-[#00AEEF] ps-5 text-lg font-light leading-relaxed text-zinc-600">
          {project.deepSummary}
        </p>
      </header>

      {/* The narrative spine */}
      <div className="mt-16 space-y-20">
        <CaseSection project={project} />
        <SynthesisSection project={project} />
        <RcaSection project={project} />
        <ProceduresSection project={project} />
      </div>
    </div>
  );
}

/* ───────────────────────────── Page ───────────────────────────────── */

/**
 * The retrospectives surface - the org's library of finished projects. The
 * library lists every past project's cost at a glance; opening one tells its
 * full story as a single scroll: the case and what it cost, how Intera stitched
 * the truth from fragmented tools, the root-cause analysis, and the procedures
 * it extracted so the same failure can't recur. Built for an executive reader -
 * minimal, sharp, one clear narrative spine.
 */
export function RetrospectivesPage() {
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);

  const openProject = openProjectId
    ? getPastProjectById(openProjectId) ?? DEFAULT_PAST_PROJECT
    : null;

  return (
    <div className="relative min-h-[100dvh] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)] text-black">
      <div className="pointer-events-none absolute inset-x-0 -top-24 h-72 bg-[radial-gradient(58%_100%_at_50%_0%,rgba(0,174,239,0.07),transparent_72%)]" />

      <CommandCenterSidebar activeTab="retrospectives" />

      <div className={SIDEBAR_WIDTH_CLASS}>
        <main className="relative z-10 w-full px-5 pb-24 pt-8 md:px-10">
          {openProject ? (
            <RetroDetail
              project={openProject}
              onBack={() => setOpenProjectId(null)}
            />
          ) : (
            <RetroLibrary projects={PAST_PROJECTS} onOpen={setOpenProjectId} />
          )}
        </main>
      </div>
    </div>
  );
}
